import { HttpQuery } from "@/interfaces/httpQuery";
import { Location } from "@/interfaces/location";
import {
  BiotrackIntegrationModel,
  ConfigValue,
  IntegrationModel,
  LocationTraceDetails,
  TraceabilityConfig,
  UserBiotrackConfig,
  UserMetrcConfig
} from "@/interfaces/traceability";
import Vue from "vue";
import HttpService from "./http.service";
import { messagesService } from "./messages.service";

class TraceabilityService extends HttpService {
  public async getTraceabilitySystems() {
    try {
      const response = await this.axios.get(
        "/services/bdi/configs_schemas/traceability"
      );
      return response.data.data.schema.enum;
    } catch (error) {
      messagesService.renderErrorMessage(error);
    }
  }

  public async getCurrentTraceability(
    query: HttpQuery = {}
  ): Promise<TraceabilityConfig[]> {
    try {
      this.uri = "/services/bdi/configs_values/traceability";
      const response = await super.get(query);
      return response.data.data;
    } catch (error) {
      return [];
    }
  }

  public async setCurrentTraceability(config: {
    location_id: number | null;
    value: string;
  }) {
    try {
      const response = await this.axios.request({
        url: "/services/bdi/configs_values/traceability",
        method: "PUT",
        data: config
      });
      return response.data.data;
    } catch (error) {
      messagesService.renderErrorMessage(error);
    }
  }

  public async deleteTraceability(locationId: number | null) {
    try {
      const response = await this.axios.request({
        url: "/services/bdi/configs_values/traceability",
        method: "DELETE",
        data: {
          location_id: locationId
        }
      });
      return response.data.data;
    } catch (error) {
      messagesService.renderErrorMessage(error);
    }
  }

  public async setStateTraceabilitySystem(
    enabled: boolean,
    locationId: number
  ) {
    try {
      const action = enabled ? "enable" : "disable";
      await this.axios.request({
        url: `${
          process.env.VUE_APP_BASE_URL_DISPENSARY
        }/integrations/metrc/${action}/${locationId}`,
        method: "PATCH"
      });
    } catch (error) {
      messagesService.renderErrorMessage(error);
    }
  }

  public async getTraceabilityConfig(
    locationIds?: number[]
  ): Promise<ConfigValue[]> {
    try {
      this.uri = "/services/bdi/configs_values/traceability_config";
      const response = await super.get({ location_id: locationIds });
      return response.data.data;
    } catch (error) {
      return [];
    }
  }

  public async getMetrcLocationValues(
    locationIds?: number[]
  ): Promise<LocationTraceDetails[]> {
    try {
      this.uri = "/services/bdi/configs_values/traceability_config";
      const response = await super.get({ location_id: locationIds });
      return response.data.data;
    } catch (error) {
      return [];
    }
  }

  public async getBiotrackTraceabilityConfig(
    locationIds?: number[]
  ): Promise<ConfigValue[]> {
    try {
      this.uri = "/services/bdi/configs_values/biotrack_traceability_config";
      const response = await super.get({ location_id: locationIds });
      return response.data.data;
    } catch (error) {
      return [];
    }
  }

  public async getBiotrackLocationValues(
    locationIds?: number[]
  ): Promise<LocationTraceDetails[]> {
    try {
      this.uri = "/services/bdi/configs_values/biotrack_traceability_config";
      const response = await super.get({ location_id: locationIds });
      return response.data.data;
    } catch (error) {
      return [];
    }
  }

  public async setIntegrationConfig(
    location: Location,
    config: IntegrationModel,
    hasConfig: boolean
  ) {
    try {
      const test = await this.testConfiguration(config, location);
      if (test) {
        const response = await this.axios.request({
          url: "/services/bdi/configs_values/traceability_config",
          method: hasConfig ? "PUT" : "POST",
          data: {
            location_id: location.id,
            value: JSON.stringify({
              location_key: config.location_key,
              license: location.license_number,
              base_uri: `${config.base_uri}:${config.port}`,
              user_credentials_required: config.user_credentials_required,
              enabled: config.enabled
            })
          }
        });
        return response.data.data;
      }
      return null;
    } catch (error) {
      return null;
    }
  }

  public async setBiotrackIntegrationConfig(
    location: Location,
    config: BiotrackIntegrationModel,
    hasConfig: boolean
  ) {
    try {
      const test = await this.testBiotrackConfiguration(config);
      if (typeof test !== "string") {
        const response = await this.axios.request({
          url: "/services/bdi/configs_values/biotrack_traceability_config",
          method: hasConfig ? "PUT" : "POST",
          data: {
            location_id: location.id,
            value: JSON.stringify({
              starting_inventory_date: `${config.starting_inventory_date}`,
              base_uri: `${config.base_uri}`,
              ubi: `${config.ubi}`,
              enabled: config.enabled
            })
          }
        });
        return response.data.data;
      }
      return test;
    } catch (error) {
      return null;
    }
  }

  public async setBiotrackTraceabilitySystem(
    enabled: boolean,
    startingInventoryDate: string,
    locationId: number
  ) {
    try {
      const action = enabled ? "enable" : "disable";

      await this.axios.request({
        url: `${
          process.env.VUE_APP_BASE_URL_DISPENSARY
        }/integrations/biotrack_traceability/${action}?starting_inventory_date=${startingInventoryDate}&location_id=${locationId}`,

        method: "PATCH"
      });
    } catch (error) {
      messagesService.renderErrorMessage(error);
    }
  }

  public async testConfiguration(config: IntegrationModel, location: Location) {
    try {
      return await this.axios.post(
        process.env.VUE_APP_BASE_URL_DISPENSARY +
          "/traceability/metrc/locations/test",
        {
          location_key: config.location_key,
          license: location.license_number,
          base_uri: `${config.base_uri}:${config.port}`,
          state_code: location.state!.code
        }
      );
    } catch (error) {
      return false;
    }
  }

  // Test Configuration method for Bio track tracability Integration
  public async testBiotrackConfiguration(config: BiotrackIntegrationModel) {
    try {
      return await this.axios.post(
        process.env.VUE_APP_BASE_URL_DISPENSARY +
          "/traceability/biotrack/locations/test",
        {
          base_uri: `${config.base_uri}`,
          ubi: `${config.ubi}`,
          enabled: config.enabled,
          starting_inventory_date: `${config.starting_inventory_date}`
        }
      );
    } catch (error) {
      return error.response.data.data;
    }
  }
  public async setBiotrackIntegrationConfigSync() {
    try {
      return await this.axios.post(
        process.env.VUE_APP_BASE_URL_DISPENSARY + "/traceability/biotrack/sync",
        {
          inventory_room: 1
        }
      );
    } catch (error) {
      return error.response.data.data;
    }
  }

  public async getUserMetrcConfig(
    userId: number
  ): Promise<UserMetrcConfig | null> {
    try {
      const response = await this.axios.get(
        process.env.VUE_APP_BASE_URL_DISPENSARY +
          "/traceability/metrc/users/" +
          userId
      );
      return response.data.data;
    } catch (error) {
      return null;
    }
  }

  public async testUserMetrcConfig(
    userConfig: UserMetrcConfig
  ): Promise<boolean> {
    try {
      const response = await this.axios.post(
        process.env.VUE_APP_BASE_URL_DISPENSARY +
          "/traceability/metrc/users/test",
        userConfig
      );
      return !!response;
    } catch (error) {
      return false;
    }
  }

  public async saveUserMetrcConfig(config: UserMetrcConfig, userId: number) {
    try {
      const response = await this.axios.post(
        process.env.VUE_APP_BASE_URL_DISPENSARY + `/traceability/metrc/users`,
        { ...config, user_id: userId }
      );
      return response.data.data;
    } catch (error) {
      messagesService.renderErrorMessage(error);
    }
  }

  public async updateUserMetrcConfig(config: UserMetrcConfig, userId: number) {
    try {
      const response = await this.axios.put(
        process.env.VUE_APP_BASE_URL_DISPENSARY +
          `/traceability/metrc/users/${userId}`,
        config
      );
      return response.data.data;
    } catch (error) {
      messagesService.renderErrorMessage(error);
    }
  }

  public async getUserBiotrackConfig(
    userId: number
  ): Promise<UserBiotrackConfig | null> {
    try {
      const response = await Vue.axios.get(
        process.env.VUE_APP_BASE_URL_DISPENSARY +
          "/traceability/biotrack/users/" +
          userId
      );
      return response.data.data;
    } catch (error) {
      return null;
    }
  }

  public async testUserBiotrackConfig(
    userConfig: UserBiotrackConfig
  ): Promise<boolean> {
    try {
      const response = await Vue.axios.post(
        process.env.VUE_APP_BASE_URL_DISPENSARY +
          "/traceability/biotrack/users/test",
        userConfig
      );
      return !!response;
    } catch (error) {
      return false;
    }
  }

  public async validateUserCredentials() {
    try {
      const response = await Vue.axios.get(
        process.env.VUE_APP_BASE_URL_DISPENSARY +
          "/traceability/biotrack/notifications"
      );
      return response.data;
    } catch (error) {
      return error;
    }
  }

  public async saveUserBiotrackConfig(
    config: UserBiotrackConfig,
    userId: number
  ) {
    try {
      const response = await Vue.axios.post(
        process.env.VUE_APP_BASE_URL_DISPENSARY +
          `/traceability/biotrack/users`,
        { ...config, user_id: userId }
      );
      return response.data.data;
    } catch (error) {
      messagesService.renderErrorMessage(error);
    }
  }

  public async updateUserBiotrackConfig(
    config: UserBiotrackConfig,
    userId: number
  ) {
    try {
      const response = await Vue.axios.put(
        process.env.VUE_APP_BASE_URL_DISPENSARY +
          `/traceability/biotrack/users/${userId}`,
        config
      );
      return response.data.data;
    } catch (error) {
      messagesService.renderErrorMessage(error);
    }
  }

  public async getStateDefault(stateCode: string) {
    try {
      const response = await this.axios.get(
        process.env.VUE_APP_BASE_URL_DISPENSARY +
          `/traceability/systems/default/${stateCode}`
      );
      return response.data.data;
    } catch (error) {
      return null;
    }
  }

  public async deleteUserTraceability(userId: number) {
    try {
      const response = await this.axios.delete(
        `${
          process.env.VUE_APP_BASE_URL_DISPENSARY
        }/traceability/metrc/users/${userId}`
      );
      return response.data;
    } catch (error) {
      messagesService.renderErrorMessage(error);
    }
  }
  public async deleteUserBiotrackTraceability(userId: number) {
    try {
      const response = await Vue.axios.delete(
        `${
          process.env.VUE_APP_BASE_URL_DISPENSARY
        }/traceability/biotrack/users/${userId}`
      );
      return response.data;
    } catch (error) {
      messagesService.renderErrorMessage(error);
    }
  }

  // API Integration for user location
  public async getUserCurrentTraceability(userId: number) {
    try {
      const response = await this.axios.get(
        `/locations/user_location_permission/${userId}`
      );
      return response.data.data;
    } catch (error) {
      return null;
    }
  }
}

export const traceabilityService = new TraceabilityService();
